import { RowData, Table as TanstackTable } from '@tanstack/table-core';
import * as React from 'react';
import { ReactErrorBoundary } from 'common/components/react-error-boundary';
import { TableReactHookOptions, useTable } from './hooks';
import { CellTypeDef } from './types';
import { ColumnFilterControl, FilterSelectionBar, FilterSelectionBarProps } from './views/filter-selection-bar';
import { TableReact, TableReactProps } from './views/table';
export type { ColumnFilterControl };
export const TableGroupContext = React.createContext<Nullable<{
  table: TanstackTable<any>;
}>>(null);
export function useTableGroupContext<TData extends RowData>(): Nullable<TanstackTable<TData>> {
  var _React$useContext;
  const {
    table
  } = (_React$useContext = React.useContext(TableGroupContext)) !== null && _React$useContext !== void 0 ? _React$useContext : {
    table: null
  };
  return table;
}
const TableGroupWrapper = function TableGroupWrapper<TData extends RowData, CustomCellTypes extends Record<string, CellTypeDef<TData, any>>>(props: TableReactHookOptions<TData, CustomCellTypes> & React.PropsWithChildren) {
  const table = useTable<TData, CustomCellTypes>(props);

  // create a new object on each render as a hack to force context consumers to update,
  // necessary because the `table` object from the TanStack Table hook returns the same object reference on each render
  // which makes the context provider believe the context hasn't changed even when the hook has triggered a state change
  const providerValue = {
    table
  };
  return <ReactErrorBoundary>
      <TableGroupContext.Provider value={providerValue}>{props.children}</TableGroupContext.Provider>
    </ReactErrorBoundary>;
};
TableGroupWrapper.Table = function <TData extends RowData>(props: Omit<TableReactProps<TData>, `table`>) {
  const table = useTableGroupContext<TData>();
  if (!table) {
    return null;
  }
  return <TableReact {...props} table={table} />;
};
TableGroupWrapper.FilterSelectionBar = function <TData extends RowData>(props: Omit<FilterSelectionBarProps<TData>, `table`>) {
  const table = useTableGroupContext<TData>();
  if (!table) {
    return null;
  }
  return <FilterSelectionBar {...props} table={table} />;
};
export const TableGroup = TableGroupWrapper;
export function Table<TData extends RowData, CustomCellTypes extends Record<string, CellTypeDef<TData, any>>>(props: Omit<TableReactProps<TData>, `table`> & TableReactHookOptions<TData, CustomCellTypes>) {
  return <TableGroup {...props} data-sentry-element="TableGroup" data-sentry-component="Table" data-sentry-source-file="index.tsx">
      <TableGroup.Table isLoading={props.isLoading} selectionLimit={props.selectionLimit} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
    </TableGroup>;
}